import React, { MutableRefObject, useRef } from 'react';
import {
  practitionersQuery_practitioners_edges as PractitionersEdges,
  practitionersQuery_practitioners_edges_node as PractitionersEdgeNode,
} from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import useInfiniteScroll, { Direction } from 'hooks/useInfiniteScroll';
import { useTranslation } from 'react-i18next';
import { Namespace } from 'i18next';
import SectionError from 'Shared/SectionError/SectionError';
import { usePractitioners } from 'Talk/hooks';
import { FiltersCollection } from '../BrowseFilterPanel/types';
import { PractitionerCard } from '../PractitionerCard';
import EmptyStateCard from '../EmptyStateCard';
import { ErrorContainer, PractitionerCardsContainer } from './styles';

export interface PractitionerCardListProps {
  filters: FiltersCollection;
}

export const PractitionerCardList = ({
  filters,
}: PractitionerCardListProps) => {
  const pageRef: MutableRefObject<null> = useRef(null);
  const { t } = useTranslation<Namespace<'talk'>>('talk');

  const noFiltersApplied = Object.values(filters).every(
    filter => filter.length === 0,
  );

  const { data, fetchMorePractitioners, loading, error, refetch } =
    usePractitioners({
      practitionersQueryWhere: {
        languages: {
          in: [...filters?.languages?.map(s => s.value)],
        },
        userUserProfileDetailConnects: {
          ...(filters.approaches.length && {
            approaches: {
              in: [...filters?.approaches?.map(s => s.value)],
            },
          }),
          ...(filters.areasOfExpertise.length && {
            and: {
              areasOfExpertise: {
                in: [...filters?.areasOfExpertise?.map(s => s.value)],
              },
            },
          }),
        },
      },
    });

  useInfiniteScroll(
    () => {
      if (data?.practitioners?.pageInfo?.hasNextPage) {
        fetchMorePractitioners();
      }
    },
    pageRef,
    Direction.DOWN,
    500,
  );

  const noPractitionersFound =
    loading ||
    (!loading && !data?.practitioners?.edges?.find(edge => edge?.node));

  if (noPractitionersFound) {
    if (error) {
      return (
        <ErrorContainer>
          <SectionError
            onRetry={() => {
              void refetch();
            }}
          />
        </ErrorContainer>
      );
    }

    return (
      <EmptyStateCard
        title={
          noFiltersApplied
            ? t('browse_practitioners.empty_state.title')
            : t('browse_practitioners.empty_state_with_filters.title')
        }
        subtitle={
          noFiltersApplied
            ? t('browse_practitioners.empty_state.subtitle')
            : t('browse_practitioners.empty_state_with_filters.subtitle')
        }
      />
    );
  }

  return (
    <PractitionerCardsContainer>
      {data?.practitioners?.edges
        ?.map(
          (edge: (PractitionersEdges | null) | null) =>
            edge?.node as PractitionersEdgeNode,
        )
        .map((practitioner: PractitionersEdgeNode) =>
          practitioner ? (
            <PractitionerCard
              key={practitioner.id}
              practitioner={practitioner}
            />
          ) : null,
        )}
    </PractitionerCardsContainer>
  );
};
